import type { DayId } from '../core/state';
import type { Messages } from '../i18n';
import type { Facing } from '../render/sprites';
import { T } from './tiles';

export type MapId = 'town' | 'school' | 'home' | 'store' | 'sports' | 'mall' | 'grandma' | 'bo' | 'market'
  | 'station' | 'taipei' | 'mrt' | 'museum' | 'hualien'
  | 'balcony'
  | 'hotel'
  // Rich trip districts (phase E): Taipei by MRT, Hualien by the tourist shuttle.
  | 'ximen' | 'longshan' | 'cks' | 'beitou' | 'hlcity'
  // Phase F: more of Taiwan by high-speed rail, and the world by plane.
  | 'airport' | 'kaohsiung' | 'tainan' | 'barcelona';
/** Phase E text namespaces (one per district package). */
export type TourNs = 'xinyi' | 'ximen' | 'cks' | 'hlcity' | 'taroko' | 'airport' | 'kaohsiung' | 'tainan' | 'barcelona';
/** Keys of one sub-table (`zones`, `objects`, `npc`) across every phase E namespace. */
export type TourKeys<K extends 'zones' | 'objects' | 'npc' | 'mapNames'> = { [N in TourNs]: keyof Messages[N][K] }[TourNs];
export type AreaKey = keyof Messages['zones'] | keyof Messages['places']['zones'] | keyof Messages['world3']['zones'] | keyof Messages['trips']['zones']
  | keyof Messages['sky']['zones'] | keyof Messages['family']['zones'] | keyof Messages['classroom']['zones'] | keyof Messages['life']['zones']
  | TourKeys<'zones'>;
export type LabelKey = keyof Messages['objects'] | keyof Messages['places']['objects'] | keyof Messages['world3']['objects'] | keyof Messages['trips']['objects']
  | keyof Messages['sky']['objects'] | keyof Messages['family']['objects'] | keyof Messages['classroom']['objects'] | keyof Messages['life']['objects']
  | TourKeys<'objects'>;

export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface Door {
  /** Trigger tiles (in tile coordinates). */
  tiles: [number, number][];
  to: MapId;
  /** Arrival point in pixels on the target map, and the facing on arrival. */
  arrive: { x: number; y: number; facing: Facing };
}

export type ObjectId =
  // counseling room
  | 'desk' | 'tea' | 'water' | 'shelfL' | 'shelfR' | 'plant' | 'sofa'
  // school
  | 'toilet' | 'easel' | 'chineseDesk' | 'piano' | 'board' | 'vending' | 'canteen' | 'libShelf' | 'atlasDesk'
  // town
  | 'benchL' | 'benchR' | 'picnic' | 'field' | 'busStop' | 'busStopW' | 'busStopE' | 'mailbox' | 'pond' | 'bush1' | 'bush2' | 'bush3' | 'kiteTree' | 'swing'
  // home
  | 'shoes' | 'sink' | 'oven' | 'homeDesk' | 'bath' | 'bed' | 'fridge' | 'tv'
  // store
  | 'shopCounter'
  // new places
  | 'nurseDesk' | 'pool' | 'gymNet' | 'tennisNet' | 'volleyNet' | 'fountain' | 'mallFountain'
  | 'cafeCounter' | 'giftCounter' | 'deptCounter' | 'restCounter'
  // homes and the night market
  | 'grTea' | 'grCooker' | 'grPhotos' | 'grSewing' | 'grCalendar' | 'boConsole' | 'boAquarium' | 'boComics' | 'boLego'
  | 'mkTea' | 'mkChicken' | 'mkBalls' | 'mkTofu' | 'mkOyster' | 'mkIce' | 'mkFish' | 'mkRings' | 'mkClaw'
  | 'fsRiddles' | 'fsTangyuan' | 'fsBoat' | 'fsZongzi' | 'fsEgg' | 'fsBBQ' | 'fsPomelo' | 'fsLion' | 'fsReunion'
  // trips
  | 'stTicket' | 'stTrain' | 'twElevator' | 'tpTrain' | 'tpNoodles' | 'tpCakes' | 'ubike'
  | 'mrtMap' | 'mrtRules' | 'mrtRide' | 'muCabbage' | 'muMeat' | 'muDing' | 'muRuware' | 'muScroll' | 'muCurio' | 'muExit'
  | 'hlHelmet' | 'hlView' | 'hlStones' | 'hlSea' | 'hlMochi' | 'hlTrain'
  | 'hlQingshui' | 'hlNineTurns' | 'hlPancake' | 'hlWonton' | 'hlAmis' | 'hlWhale' | 'htDesk' | 'htBed'
  // phase D packages use a prefix each, so they never collide
  | `sky_${string}` | `fam_${string}` | `cls_${string}` | `life_${string}`
  // phase E district packages
  | `xy_${string}` | `xm_${string}` | `ck_${string}` | `hc_${string}` | `tk_${string}`
  // phase F: airport + shared city services (toilets, rentals, banks), Kaohsiung, Tainan, Barcelona
  | `ap_${string}` | `wc_${string}` | `rent_${string}` | `bank_${string}` | `kh_${string}` | `tn_${string}` | `bc_${string}`;

/** What a map marker stands for (the minimap draws one icon per kind). */
export type PoiKind = 'transport' | 'rental' | 'toilet' | 'bank' | 'food' | 'shop' | 'sight' | 'hotel' | 'info';

export interface WorldObject {
  id: ObjectId;
  map: MapId;
  /** Footprint on the floor in pixels; blocks movement when `solid`. */
  rect: Rect;
  solid: boolean;
  label: LabelKey;
  icon: string;
  days?: DayId[];
  /** How far above the footprint a tap still hits it (tall props like trees). */
  tall?: number;
  /** Only there during this festival week. */
  fest?: string;
  /** Shown on the minimap as a point of interest. */
  poi?: PoiKind;
}

/** Purely decorative, depth-sorted props (trees, tables, shelves...). */
export type DecorKind =
  | 'tree' | 'cherry' | 'pine' | 'bushDeco' | 'flowerbed' | 'lamp' | 'hydrant' | 'booth' | 'goal' | 'hoop' | 'slide'
  | 'armchairs' | 'teaTable' | 'pottedPlant' | 'lockers' | 'studentDesk' | 'teacherDesk' | 'blackboard' | 'bookshelf'
  | 'readingTable' | 'cafeTable' | 'shoeLockers' | 'stall' | 'sinkRow' | 'diningTable' | 'livingSofa' | 'tvStand' | 'rugRound'
  | 'storeShelf' | 'storeFridge' | 'counterTill' | 'clock' | 'poster' | 'window' | 'painting' | 'curtain' | 'mapBoard'
  | 'drumKit' | 'vendingDeco' | 'trashBin' | 'bikeRack' | 'signSchool' | 'signStore' | 'toyBox' | 'wardrobe' | 'bookshelfHome'
  | 'tennisNet' | 'volleyNet' | 'fountain' | 'signSports' | 'signMall' | 'parkedCar' | 'hay' | 'trough'
  | 'kitchenCounter' | 'altar' | 'woodChair' | 'fanStand' | 'catBasket' | 'bunkBed' | 'shoeCabinet' | 'laundryRack'
  | 'lanternString' | 'stool' | 'foodTable' | 'scooter' | 'marketGate' | 'festLanterns' | 'festCouplet'
  | 'departures' | 'stationBench' | 'ticketGates' | 'tower101' | 'sign101' | 'planter' | 'trafficLight' | 'mrtSign' | 'stationSign'
  | 'shopSign' | 'adPanel' | 'escalator' | 'screenDoors' | 'hangingScroll' | 'museumBanner' | 'stanchion' | 'pillar' | 'museumBench'
  | 'waterfall' | 'shrine' | 'tunnel' | 'ropeBridgeRails' | 'trailSign' | 'lookout' | 'stoneCairn' | 'stationArrow'
  | 'tarokoGate' | 'hualienSign' | 'pier'
  | `sky_${string}` | `fam_${string}` | `cls_${string}` | `life_${string}`
  | `xy_${string}` | `xm_${string}` | `ck_${string}` | `hc_${string}` | `tk_${string}` | `tw_${string}` | `ap_${string}` | `kh_${string}` | `tn_${string}` | `bc_${string}`
  | 'gymCourt' | 'scoreboard' | 'lifeguardChair' | 'benchIndoor' | 'ballCart' | 'menuBoard' | 'giftShelf' | 'clothesRack'
  | 'mannequin' | 'easelDeco' | 'paintShelf' | 'nurseBed' | 'medCabinet' | 'curtainScreen' | 'musicStands' | 'xylophone';

export interface Decor {
  kind: DecorKind;
  /** Anchor: bottom-centre for standing props, top-left for wall props. */
  x: number;
  y: number;
  w?: number;
  h?: number;
  /** Optional collision footprint. */
  solid?: Rect;
  /** Wall-mounted decor is drawn on the ground layer, below everything. */
  wall?: boolean;
  variant?: number;
  /** Only shown during this festival week. */
  fest?: string;
}

export interface Area {
  key: AreaKey;
  rect: Rect;
}

export interface Light {
  x: number;
  y: number;
  r: number;
  color: string;
}

export interface Theme {
  wallFace: string;
  wallLight: string;
  wainscot: string;
  wallTop: string;
}

export interface MapDef {
  id: MapId;
  w: number;
  h: number;
  rows: string[];
  indoor: boolean;
  theme: Theme;
  doors: Door[];
  decor: Decor[];
  areas: Area[];
  lights: Light[];
  /**
   * Named arrival points for maps reached without a door: `mrt` (Taipei districts),
   * `train`, `shuttle`, `taroko`, `qixingtan` (Hualien), `hotelDoor`…
   */
  spots?: Record<string, Spot>;
}

export interface Spot {
  x: number;
  y: number;
  facing: Facing;
}

/** Tile → pixel helpers. */
export const px = (t: number) => t * T;
export const tileRect = (tx: number, ty: number, tw = 1, th = 1): Rect => ({ x: tx * T, y: ty * T, w: tw * T, h: th * T });
/** A footprint that sits at the bottom of a tile span, `h` pixels tall. */
export const foot = (tx: number, ty: number, tw: number, h = 10, inset = 2): Rect => ({
  x: tx * T + inset,
  y: (ty + 1) * T - h,
  w: tw * T - inset * 2,
  h,
});

/** Grid editor used by the map definitions. */
export class MapBuilder {
  readonly grid: string[][];

  constructor(
    readonly w: number,
    readonly h: number,
    fillWith: string,
  ) {
    this.grid = Array.from({ length: h }, () => Array<string>(w).fill(fillWith));
  }

  set(x: number, y: number, ch: string): this {
    if (x >= 0 && y >= 0 && x < this.w && y < this.h) this.grid[y][x] = ch;
    return this;
  }

  get(x: number, y: number): string {
    return this.grid[y]?.[x] ?? ' ';
  }

  fill(x: number, y: number, w: number, h: number, ch: string): this {
    for (let j = y; j < y + h; j++) for (let i = x; i < x + w; i++) this.set(i, j, ch);
    return this;
  }

  /** Walled room: '#' border, `floor` inside. Shares walls with neighbours. */
  room(x: number, y: number, w: number, h: number, floor: string): this {
    for (let j = y; j < y + h; j++) {
      for (let i = x; i < x + w; i++) {
        const edge = i === x || j === y || i === x + w - 1 || j === y + h - 1;
        this.set(i, j, edge ? '#' : floor);
      }
    }
    return this;
  }

  /**
   * A building seen from the south: `roofRows` of roof, then wall rows with
   * windows; `doors` are x offsets on the bottom wall row.
   */
  building(
    x: number,
    y: number,
    w: number,
    h: number,
    o: { roof: string; wall: string; window: string; roofRows: number; doors: number[]; windows: number[]; awning?: boolean },
  ): this {
    this.fill(x, y, w, o.roofRows, o.roof);
    const wallRows = h - o.roofRows;
    for (let j = 0; j < wallRows; j++) {
      const ty = y + o.roofRows + j;
      for (let i = 0; i < w; i++) {
        let ch = o.wall;
        if (o.awning && j === 0) ch = 'A';
        else if (o.windows.includes(i)) ch = o.window;
        if (j === wallRows - 1 && o.doors.includes(i)) ch = 'D';
        this.set(x + i, ty, ch);
      }
    }
    return this;
  }

  rows(): string[] {
    return this.grid.map((r) => r.join(''));
  }
}
